import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { FiCheck, FiX, FiZap } from "react-icons/fi";
import { fetchPlanState, fetchPlans } from "../lib/billingApi";
import { useUpgradeModal } from "../Components/UpgradeModal";
import { displayTierLabel, planLabel } from "../utils/tier";

const TIER_ORDER = ["free", "starter", "pro", "business", "agency"];

// Numeric limits shown per plan card (from the catalog `limits` block).
const LIMIT_ROWS = [
    ["max_responses_per_month", "Responses / mo"],
    ["max_ai_generations_per_month", "AI generations / mo"],
    ["max_workflows_per_form", "Agent workflows / form"],
    ["max_active_forms", "Active forms"],
    ["max_seats", "Team seats"],
];

// Marquee features shown per plan card (boolean features from the catalog).
const MARQUEE = [
    ["response_intelligence_advanced", "Advanced Response Intelligence"],
    ["agent_actions", "Agent Actions (workflows)"],
    ["custom_thank_you_pages", "Custom thank-you pages"],
    ["remove_branding", "Remove ContentFlashAI branding"],
    ["api_access", "API access"],
    ["team_seats", "Team seats"],
    ["sso", "SSO & audit logs"],
    ["custom_domain", "Custom domain"],
    ["white_label", "White-label branding"],
];

function price(plan, currency, cycle) {
    if (!plan) return "—";
    if (plan.tier === "free") return currency === "INR" ? "₹0" : "$0";
    if (currency === "INR") {
        const paise = cycle === "yearly" ? plan.pricing.inr.yearlyPaise : plan.pricing.inr.monthlyPaise;
        return `₹${(paise / 100).toLocaleString("en-IN")}`;
    }
    const cents = cycle === "yearly" ? plan.pricing.usd.yearlyCents : plan.pricing.usd.monthlyCents;
    return `$${(cents / 100).toFixed(0)}`;
}

// Row formatter: -1 → Unlimited, 0 → not available on this plan.
const fmtRow = (v) => (v === -1 ? "Unlimited" : v === 0 ? "—" : Number(v).toLocaleString());

export default function PlansPage() {
    const { openUpgradeModal } = useUpgradeModal();
    const [planState, setPlanState] = useState(null);
    const [plans, setPlans] = useState([]);
    const [currency, setCurrency] = useState(() => localStorage.getItem("cfai_currency") || "INR");
    const [cycle, setCycle] = useState("yearly");
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [ps, pl] = await Promise.all([fetchPlanState(), fetchPlans()]);
            setPlanState(ps);
            setPlans((pl.plans || []).filter((p) => TIER_ORDER.includes(p.tier)));
            if (!localStorage.getItem("cfai_currency")) setCurrency(ps.currency || "INR");
        } catch (e) { /* surfaced as empty state */ } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        const h = () => load();
        window.addEventListener("cfai:plan-changed", h);
        return () => window.removeEventListener("cfai:plan-changed", h);
    }, [load]);

    const setCur = (c) => { setCurrency(c); localStorage.setItem("cfai_currency", c); };

    const effective = planState?.effective_plan || "free";
    const effectiveOrd = TIER_ORDER.indexOf(effective);
    const ltd = !!planState?.has_ltd;

    const onPick = (slug) =>
        openUpgradeModal({ trigger: "manual", recommendedPlan: slug, dismissable: true });

    return (
        <div className="min-h-screen max-w-7xl mx-auto px-4 sm:px-6 py-8 font-Rajdhani text-gray-900 dark:text-white">
            <Helmet><title>Plans - Content Flash AI</title></Helmet>

            {/* Header */}
            <div className="text-center mb-8">
                <h1 className="text-3xl font-bold">{ltd ? "Your lifetime tier + optional add-ons" : "Choose your plan"}</h1>
                <p className="text-gray-500 dark:text-gray-400 mt-1">
                    {ltd ? "Your AppSumo lifetime baseline stays active — add a subscription only for features beyond it."
                         : "Start free. Upgrade as you grow. Cancel anytime."}
                </p>
            </div>

            {/* LTD card */}
            {ltd && (
                <div className="mb-8 rounded-2xl border-2 border-indigo-200 dark:border-indigo-700/50 bg-indigo-50/50 dark:bg-indigo-900/15 p-6">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                            <span className="inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full bg-indigo-600 text-white"><FiZap className="w-3.5 h-3.5" /> AppSumo Lifetime</span>
                            <h2 className="text-xl font-bold mt-2">AppSumo Lifetime {displayTierLabel(planState.ltd_tier)}</h2>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{planState.ltd_code_count} code{planState.ltd_code_count === 1 ? "" : "s"} stacked · baseline never expires</p>
                        </div>
                        <div className="flex gap-2">
                            <Link to="/redeem" className="px-4 py-2 rounded-xl border border-indigo-300 dark:border-indigo-600 text-indigo-700 dark:text-indigo-300 text-sm font-semibold hover:bg-indigo-100 dark:hover:bg-indigo-900/30 no-underline">Stack another code →</Link>
                        </div>
                    </div>
                </div>
            )}

            {/* Toggles */}
            <div className="flex items-center justify-center gap-3 mb-8">
                <div className="inline-flex p-0.5 bg-gray-100 dark:bg-gray-800 rounded-lg text-sm font-medium">
                    <button onClick={() => setCycle("yearly")} className={`px-3 py-1.5 rounded-md ${cycle === "yearly" ? "bg-white dark:bg-gray-700 shadow-sm" : "text-gray-500"}`}>Annual <span className="text-indigo-600 dark:text-indigo-400">−17%</span></button>
                    <button onClick={() => setCycle("monthly")} className={`px-3 py-1.5 rounded-md ${cycle === "monthly" ? "bg-white dark:bg-gray-700 shadow-sm" : "text-gray-500"}`}>Monthly</button>
                </div>
                <div className="inline-flex p-0.5 bg-gray-100 dark:bg-gray-800 rounded-lg text-sm font-medium">
                    <button onClick={() => setCur("USD")} className={`px-3 py-1.5 rounded-md ${currency === "USD" ? "bg-white dark:bg-gray-700 shadow-sm" : "text-gray-500"}`}>USD</button>
                    <button onClick={() => setCur("INR")} className={`px-3 py-1.5 rounded-md ${currency === "INR" ? "bg-white dark:bg-gray-700 shadow-sm" : "text-gray-500"}`}>INR</button>
                </div>
            </div>

            {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
                    {Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-96 rounded-2xl bg-gray-100 dark:bg-gray-800 animate-pulse" />)}
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
                    {plans.map((p) => {
                        const ord = TIER_ORDER.indexOf(p.tier);
                        const isCurrent = p.tier === effective;
                        const isUpgrade = ord > effectiveOrd;
                        const popular = p.tier === "pro";
                        return (
                            <div key={p.slug} className={`relative flex flex-col rounded-2xl border p-5 bg-white dark:bg-[#23263a] ${isCurrent ? "border-indigo-500 ring-2 ring-indigo-500/30" : popular ? "border-indigo-200 dark:border-indigo-700/50" : "border-gray-200 dark:border-gray-700/60"}`}>
                                {popular && !isCurrent && <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-xs font-bold uppercase tracking-wide px-2.5 py-0.5 rounded-full bg-indigo-600 text-white">Popular</span>}
                                <h3 className="font-bold text-xl">{p.name}</h3>
                                <div className="mt-1 flex items-baseline gap-1">
                                    <span className="text-3xl font-bold font-display">{price(p, currency, cycle)}</span>
                                    {p.tier !== "free" && <span className="text-sm text-gray-400">/{cycle === "yearly" ? "yr" : "mo"}</span>}
                                </div>
                                <div className="mt-4 space-y-2 border-y border-gray-100 dark:border-gray-700/60 py-4">
                                    {LIMIT_ROWS.map(([key, label]) => (
                                        <div key={key} className="flex items-center justify-between gap-2 text-sm">
                                            <span className="text-gray-500 dark:text-gray-400">{label}</span>
                                            <span className="font-semibold text-gray-800 dark:text-gray-200 text-right">{fmtRow(p.limits[key])}</span>
                                        </div>
                                    ))}
                                </div>

                                <ul className="mt-4 space-y-2 flex-1">
                                    {MARQUEE.map(([key, label]) => {
                                        const has = !!p.features[key];
                                        return (
                                            <li key={key} className={`flex items-start gap-2 text-sm ${has ? "text-gray-700 dark:text-gray-300" : "text-gray-400 dark:text-gray-600 line-through"}`}>
                                                {has ? <FiCheck className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> : <FiX className="w-4 h-4 mt-0.5 flex-shrink-0" />}
                                                {label}
                                            </li>
                                        );
                                    })}
                                </ul>

                                <div className="mt-5">
                                    {isCurrent ? (
                                        <div className="w-full py-2 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-sm font-semibold text-center">
                                            {ltd ? "Your baseline" : "Current plan"}
                                        </div>
                                    ) : isUpgrade ? (
                                        <button onClick={() => onPick(p.slug)} className="w-full py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition">
                                            {ltd ? `Add ${p.name}` : `Upgrade to ${p.name}`}
                                        </button>
                                    ) : (
                                        <div className="w-full py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-400 text-sm font-medium text-center">Included</div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {ltd && (
                <p className="text-center text-sm text-gray-600 dark:text-gray-400 mt-6">
                    Features marked unavailable on your tier require an active subscription — your lifetime baseline ({planLabel(effective)}-equivalent) always stays.
                </p>
            )}
        </div>
    );
}
